// Geführte Einmal-Hinweise im TTS-Flow (2026-10-03, Philip nach dem Test mit
// einer Erstnutzerin: „ein paar mehr von den Hilfs-Popups einmalig am Anfang").
// Fünf Stationen, immer nur eine sichtbar, jede mit × schließbar und über den
// 7-Tage-Mechanismus aus onceFlag.ts pro Gerät gemerkt:
//
//   autosplit  — Blöcke sind ins Bild gescrollt, der Text ist noch EIN Block
//                mit mehreren Sätzen → über dem Auto-Split-Button.
//   generate   — noch kein Take → Sprechblase über „Generate All" mit zwei
//                gebogenen Pfeilen auf „Generate All" und „Generate Block N".
//   (star)     — der bestehende Stern-Hinweis am ersten Take (lib/starHint.ts)
//                übernimmt diese Station; hier nur ausgespart.
//   timeline   — erster Favorit liegt auf der Timeline → Hinweis unter dem
//                Timeline-Band, dass sich der Take verschieben lässt.
//   upload     — jeder Block hat einen Favoriten → über „Upload Version".
//
// Die Reihenfolge ist fest; eine Station, deren Phase vorbei ist (z. B. Takes
// kommen, während „generate" noch steht), gilt als gesehen, damit sie nach
// dem Upload-Reset der Takes nicht wieder auftaucht. Reine Funktionen, damit
// die Sequenz ohne DOM testbar ist.

import { hasSeenOnceFlag, markOnceFlagSeen } from './onceFlag';

export const COACH_MARK_IDS = ['autosplit', 'generate', 'timeline', 'upload'] as const;
export type CoachMarkId = (typeof COACH_MARK_IDS)[number];

export const COACH_MARK_KEYS: Record<CoachMarkId, string> = {
  autosplit: 'vs-coach-autosplit-v1',
  generate: 'vs-coach-generate-v1',
  timeline: 'vs-coach-timeline-v1',
  upload: 'vs-coach-upload-v1',
};

export const COACH_MARK_TEXTS: Record<CoachMarkId, string> = {
  autosplit: 'Click here to split the text into sentences and generate each one on its own.',
  generate: 'Click here to generate the first take.',
  timeline: 'Your take is on the timeline. Drag it left or right to set exactly when it starts.',
  upload: 'Sounds good? Timing correct? Upload your version here.',
};

export function hasSeenCoachMark(id: CoachMarkId, storage?: Storage): boolean {
  return hasSeenOnceFlag(COACH_MARK_KEYS[id], storage);
}

export function markCoachMarkSeen(id: CoachMarkId, storage?: Storage): void {
  markOnceFlagSeen(COACH_MARK_KEYS[id], storage);
}

export function readSeenCoachMarks(storage?: Storage): Set<CoachMarkId> {
  return new Set(COACH_MARK_IDS.filter((id) => hasSeenCoachMark(id, storage)));
}

export interface CoachMarkState {
  /** Bereits gesehene (oder überholte) Stationen. */
  seen: ReadonlySet<CoachMarkId>;
  /** Die Block-Spalte war schon einmal ausreichend im Bild (ScrollHint-Regel). */
  blocksSeen: boolean;
  /** Genau ein Block, der mehr als einen Satz enthält — Auto-Split lohnt sich. */
  canSplit: boolean;
  /** Irgendein Block hat mindestens einen Take. */
  hasTakes: boolean;
  /** Irgendein Block hat einen Favoriten. */
  hasFav: boolean;
  /** Jeder Block hat Text und einen Favoriten — Upload freigeschaltet. */
  allReady: boolean;
}

/** Welche Station ist jetzt dran? `null` = keine. */
export function pickCoachMark(s: CoachMarkState): CoachMarkId | null {
  if (!s.hasTakes) {
    if (!s.blocksSeen) return null;
    if (s.canSplit && !s.seen.has('autosplit')) return 'autosplit';
    if (!s.seen.has('generate')) return 'generate';
    return null;
  }
  // Zwischen erstem Take und erstem Favoriten spricht der Stern-Hinweis.
  if (!s.hasFav) return null;
  if (!s.seen.has('timeline')) return 'timeline';
  if (s.allReady && !s.seen.has('upload')) return 'upload';
  return null;
}

/** Stationen, deren Phase der Zustand bereits hinter sich hat — sie gelten
 *  als gesehen, auch ohne Klick aufs ×. */
export function passedCoachMarks(s: Pick<CoachMarkState, 'hasTakes' | 'hasFav' | 'allReady'> & {
  /** Ein Chip wurde vom Talent selbst verschoben (`Block.pinned`). */
  dragged: boolean;
  /** Es wurde in dieser Session schon hochgeladen. */
  uploaded: boolean;
}): CoachMarkId[] {
  const out: CoachMarkId[] = [];
  if (s.hasTakes) out.push('autosplit', 'generate');
  if (s.hasFav && s.dragged) out.push('timeline');
  if (s.uploaded) out.push('timeline', 'upload');
  return out;
}
